import moment from 'moment';

/**
 * Turns the widget endpoint's flat rows into the shape Recharts expects, and
 * decides how each quantity should be drawn.
 *
 * The mark carries the meaning here, not the colour:
 *   - a rate is continuous, so it is a line and the area beneath it is volume;
 *   - a totaliser is a counter that holds then jumps, so it steps and is never
 *     filled — the area under a cumulative curve integrates to nothing useful;
 *   - consumption per period is a set of discrete buckets, so it is bars; a
 *     line would imply readings between the days.
 */

/** How a series should be drawn. */
export const Mark = { BARS: 'bars', STEP: 'step', RATE: 'rate', LINE: 'line' };

/**
 * Graph type to the mark that suits its quantity.
 *
 * The water dashboard's quantities are all per-period totals, so one series
 * draws as bars — a line between two daily totals implies readings that do not
 * exist. Several series compared over the same periods draw as lines instead,
 * because the eye can follow a line across a chart and cannot follow eight
 * groups of three bars, and an area fill per series would obscure the ones
 * behind it.
 */
const MARK_BY_GRAPH = {
  netConsumption: Mark.BARS,
  massTotalizer: Mark.STEP,
  instantaneousFlow: Mark.RATE,
  periodTotal: Mark.BARS,
  seriesComparison: Mark.LINE,
};

/**
 * Chooses the mark for a graph.
 * @param {string} graphType - netConsumption, massTotalizer or instantaneousFlow.
 * @returns {string} One of Mark.
 */
export const markFor = (graphType) => MARK_BY_GRAPH[graphType] || Mark.RATE;

/**
 * Reports whether a mark's axis must include zero.
 *
 * A rate, a per-period total and a comparison between them are magnitudes
 * from nothing, so zero is meaningful — and a flat line at zero is the outage
 * signal, which a cropped axis would hide. A totaliser reading 487 m³ drawn
 * from zero is a flat line carrying no information, so it crops.
 *
 * @param {string} mark - One of Mark.
 * @returns {boolean} True when the axis should start at zero.
 */
export const isZeroBased = (mark) => mark !== Mark.STEP;

/**
 * Resolves the y-axis domain for a set of values.
 *
 * A cropped axis leaves headroom so the line does not sit flush against the
 * frame; a third of the range, following the ONS guidance.
 *
 * @param {Array<number|null>} values - Plotted values, nulls ignored.
 * @param {string} mark - One of Mark.
 * @returns {Array<number>} `[min, max]` for the axis.
 */
export const axisDomain = (values = [], mark = Mark.RATE) => {
  const real = values.filter((v) => v != null && Number.isFinite(Number(v))).map(Number);
  if (!real.length) return [0, 1];

  const lo = Math.min(...real);
  const hi = Math.max(...real);

  if (isZeroBased(mark)) return [0, hi === 0 ? 1 : niceCeil(hi)];

  const pad = (hi - lo || Math.abs(hi) * 0.01 || 1) / 3;

  /* A totaliser only counts upwards, so padding below zero would invent a
     region the quantity can never occupy — and with two series an order of
     magnitude apart the pad is large enough to do exactly that. */
  const floor = lo >= 0 ? Math.max(0, lo - pad) : lo - pad;

  return [niceFloor(floor), niceCeil(hi + pad / 3)];
};

/**
 * Rounds a bound down to a readable step, so ticks land on whole numbers.
 * @param {number} value - The raw bound.
 * @returns {number} The rounded bound.
 */
const niceFloor = (value) => {
  if (value === 0) return 0;
  const step = niceStep(value);
  return Math.floor(value / step) * step;
};

/**
 * Rounds a bound up to a readable step.
 * @param {number} value - The raw bound.
 * @returns {number} The rounded bound.
 */
const niceCeil = (value) => {
  if (value === 0) return 0;
  const step = niceStep(value);
  return Math.ceil(value / step) * step;
};

/**
 * A rounding step suited to the magnitude, so an axis reads 25,000 rather
 * than 24,936 and never 81.093333333334.
 * @param {number} value - The bound being rounded.
 * @returns {number} The step to round to.
 */
const niceStep = (value) => {
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(value))));
  return magnitude / 2;
};

/**
 * Abbreviates an axis tick, leaving small numbers alone.
 *
 * Below ten thousand the abbreviation costs precision without saving space, so
 * it only kicks in above that. Tooltips always show the full value.
 *
 * @param {number} value - Tick value.
 * @returns {string} Tick label.
 */
export const compactTick = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return '';
  const n = Number(value);
  if (Math.abs(n) >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${+(n / 1_000).toFixed(1)}k`;
  return `${+n.toFixed(2)}`;
};

/**
 * Pivots the flat widget rows into one row per period.
 *
 * @param {Array<object>} rows - Widget rows with `period_ts` and `sum_value`.
 * @param {string} stackByField - Field naming each series, e.g. 'kpi'.
 * @param {string} fallbackName - Series name when the response is not stacked.
 * @returns {{data: Array<object>, series: Array<{key: string, name: string}>}}
 */
export const pivotSeries = (rows = [], stackByField, fallbackName = 'value') => {
  const byPeriod = new Map();
  const keys = [];

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (!row?.period_ts) return;

    const key = String((stackByField && row[stackByField]) || fallbackName);
    if (!keys.includes(key)) keys.push(key);

    if (!byPeriod.has(row.period_ts)) {
      byPeriod.set(row.period_ts, { ts: row.period_ts, x: row.displayPeriods || row.period_ts });
    }
    const value = Number(row.sum_value ?? row.value);
    byPeriod.get(row.period_ts)[key] = Number.isFinite(value) ? value : null;
  });

  const data = Array.from(byPeriod.values()).sort(
    (a, b) => new Date(a.ts) - new Date(b.ts)
  );

  return { data, series: keys.map((key) => ({ key, name: key })) };
};

/**
 * Marks the periods a meter never reported, so the line breaks there.
 *
 * The query gap-fills and coalesces a missing reading to 0, which makes a dead
 * gateway indistinguishable from a closed valve. Recharts only breaks a line
 * where the value is explicitly null, so the zeroes that sit outside the
 * meter's reporting span are restored to null and the run between the first
 * and last real reading is left alone — an outage in the middle of the data is
 * a finding, not padding.
 *
 * @param {Array<object>} data - Pivoted rows.
 * @param {Array<{key: string}>} series - Series descriptors.
 * @returns {Array<object>} Rows with unreported periods nulled.
 */
export const openGaps = (data = [], series = []) => {
  const keys = series.map((s) => s.key);
  const total = (row) => keys.reduce((sum, k) => sum + Math.abs(Number(row[k] || 0)), 0);

  const first = data.findIndex((row) => total(row) !== 0);
  if (first === -1) return data.map((row) => ({ ...row, ...nulls(keys) }));

  let last = data.length - 1;
  while (last > first && total(data[last]) === 0) last -= 1;

  return data.map((row, index) =>
    index < first || index > last ? { ...row, ...nulls(keys) } : row
  );
};

/**
 * Builds an object setting every series key to null.
 * @param {Array<string>} keys - Series keys.
 * @returns {object} Key to null.
 */
const nulls = (keys) => keys.reduce((acc, k) => ({ ...acc, [k]: null }), {});

/**
 * Finds the runs of unreported periods, so they can be shaded on the chart.
 *
 * A span that reaches the last period is marked `open`: the meter is still
 * silent as the window ends, so whatever the meter reports about itself now is
 * the reason it is missing. A span that closed again — the meter went quiet on
 * Tuesday and came back on Wednesday — carries no such claim, because nothing
 * records what was wrong on a given past day.
 *
 * @param {Array<object>} data - Rows already passed through `openGaps`.
 * @param {Array<{key: string}>} series - Series descriptors.
 * @returns {Array<{from: string, to: string, open: boolean}>} Spans with no readings.
 */
export const gapSpans = (data = [], series = []) => {
  const keys = series.map((s) => s.key);
  const missing = (row) => keys.every((k) => row[k] == null);

  const spans = [];
  let start = null;

  data.forEach((row, index) => {
    if (missing(row)) {
      if (start === null) start = row.x;
      if (index === data.length - 1) spans.push({ from: start, to: row.x, open: true });
    } else if (start !== null) {
      spans.push({ from: start, to: data[index - 1].x, open: false });
      start = null;
    }
  });

  return spans;
};

/**
 * Formats a period for the x axis, at the density its aggregation implies.
 * @param {string} aggregation - minute, hour, day or month.
 * @returns {Function} Tick formatter.
 */
export const tickFormatterFor = (aggregation) => (value) => {
  const at = moment(value, moment.ISO_8601);
  if (!at.isValid()) return value;
  if (aggregation === 'minute' || aggregation === 'hour') return at.format('HH:mm');
  if (aggregation === 'month') return at.format('MMM YY');
  return at.format('DD MMM');
};

/**
 * Formats a period for the tooltip heading.
 *
 * The axis ticks are abbreviated because they repeat across the width of the
 * chart; the tooltip names a single period the reader has pointed at, so it
 * carries the year and, where the aggregation implies one, the time. An axis
 * tick reading '01 Aug' is enough beside its neighbours — on its own it is not.
 *
 * @param {string} aggregation - minute, hour, day or month.
 * @returns {Function} Label formatter.
 */
export const labelFormatterFor = (aggregation) => (value) => {
  const at = moment(value, moment.ISO_8601);
  if (!at.isValid()) return value;
  if (aggregation === 'minute') return at.format('DD MMM YYYY, HH:mm');
  if (aggregation === 'hour') return at.format('DD MMM YYYY, HH:00');
  if (aggregation === 'month') return at.format('MMMM YYYY');
  return at.format('DD MMM YYYY');
};

/**
 * What each graph type is measuring, for the y-axis title.
 *
 * The mark tells the reader how the quantity behaves; it does not tell them
 * what the quantity is. A column of numbers running 0 to 100 could be litres,
 * cubic metres or a percentage, and the reader has to already know which —
 * which is exactly the knowledge a dashboard exists to supply.
 */
const QUANTITY_BY_GRAPH = {
  netConsumption: 'Consumption',
  massTotalizer: 'Cumulative reading',
  instantaneousFlow: 'Flow rate',
  periodTotal: 'Volume',
  seriesComparison: 'Volume',
};

/**
 * Titles the y axis with the quantity and its unit.
 * @param {string} graphType - netConsumption, massTotalizer, instantaneousFlow,
 *   periodTotal or seriesComparison.
 * @param {string} unit - Unit the values are in.
 * @returns {string} The axis title.
 */
export const yAxisLabelFor = (graphType, unit) => {
  const quantity = QUANTITY_BY_GRAPH[graphType] || 'Value';
  return unit ? `${quantity} (${unit})` : quantity;
};

/**
 * Titles the x axis with what its ticks are.
 *
 * The wording follows `tickFormatterFor`, so the title describes the ticks the
 * reader is actually looking at rather than the field behind them: an hourly
 * chart ticks in clock time and a monthly one in months.
 *
 * @param {string} aggregation - minute, hour, day or month.
 * @returns {string} The axis title.
 */
export const xAxisLabelFor = (aggregation) => {
  if (aggregation === 'minute' || aggregation === 'hour') return 'Time';
  if (aggregation === 'month') return 'Month';
  return 'Date';
};
